import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { GenericObject, PaymentGridData, PaymentGridRequestOptions, OfferType } from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import * as types from '../types/actionTypes';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../types/actionTypes';
import type { AddFlexBuyPaymentGridMetaOptions } from '../types/commonTypes';
import type { AnyAction } from 'redux';
import type { FlexBuyPaymentGridDataItems, UpdateFlexBuyPaymentGridDataPayload } from '../types/flexBuyTypes';
import { PAYMENT_REQUEST_OVERRIDE_NODE } from '../constants';

export const PAYMENT_GRID_AXIS_SIZE = 3;

export const padPaymentGridAxis = <T>(axis: (T | null)[]): (T | null)[] =>
    axis.length >= PAYMENT_GRID_AXIS_SIZE ? axis : [...axis, ...new Array(PAYMENT_GRID_AXIS_SIZE - axis.length).fill(null)];

export const getGridTerms = (allTerms: number[], currentTerm: number): number[] => {
    // If allTerms is null or empty, return an empty array
    if (!allTerms || allTerms.length === 0) {
        return [];
    }

    // If allTerms has 1 to 3 elements, return allTerms
    if (allTerms.length >= 1 && allTerms.length <= PAYMENT_GRID_AXIS_SIZE) {
        return [...allTerms, null, null].slice(0, PAYMENT_GRID_AXIS_SIZE) as number[];
    }

    // Sort allTerms in ascending order
    const sortedTerms = allTerms.sort((a, b) => a - b);

    // If currentTerm is undefined, return the first three terms
    if (!currentTerm) {
        return sortedTerms.slice(0, PAYMENT_GRID_AXIS_SIZE);
    }

    // If currentTerm is the first term, return the first three terms
    if (sortedTerms[0] === currentTerm) {
        return sortedTerms.slice(0, PAYMENT_GRID_AXIS_SIZE);
    }

    // If currentTerm is the last term, return the last three terms
    if (sortedTerms[sortedTerms.length - 1] === currentTerm) {
        return sortedTerms.slice(-PAYMENT_GRID_AXIS_SIZE);
    }

    // If currentTerm is neither the first nor the last term,
    // return the term before, the current term, and the term after
    const indexOfCurrentTerm = sortedTerms.indexOf(currentTerm);
    return sortedTerms.slice(indexOfCurrentTerm - 1, indexOfCurrentTerm + 2);
};

/**
 * `PaymentGridRequestOptions` lives in the published dr-platform-types package, so grid-only meta with no
 * consumer outside this repo is extended here instead of there.
 */
export type AddPaymentGridMetaOptions = PaymentGridRequestOptions & {
    /**
     * Suppresses the `programInfoByTerm` merge this response would perform, so a column keeps the single program resolved
     * for it instead of being repinned by whichever row responded last. Lease per-row grid payments only.
     */
    skipProgramInfoUpdate?: boolean;
    /** Identifies the rows fanned out from ONE grid edit, so the latest-call-wins guard treats them as one round. */
    gridRoundId?: string;
    /**
     * Identifies a call belonging to a full grid **build** - the discovery and the rows it fans out. Separate from
     * `gridRoundId` because supersession is by build: a rate edit must not invalidate the open's untouched columns.
     */
    gridBuildId?: string;
    /**
     * Every term the round asked about, across all of its calls. The settling call's own `termList` is not enough: a split
     * row is two calls at one cash-down key, and the half that settled last judged only its own terms.
     */
    gridRoundTerms?: number[];
    gridRoundColumns?: number[];
    /** The Auto Select Program state this call went out under, so the verdict its response triggers is judged against it. Lease grid rows only. */
    isUserProgramQuotes?: boolean;
    /**
     * This row call is the grid **multiplying a deal-screen edit**, not an edit made inside the grid. `gridAction` cannot
     * say that: every payment action is fanned out into row calls stamped with it while the grid is open.
     */
    dealScreenOriginated?: boolean;
};

/**
 * One id per fan-out of grid row payments. A grid edit becomes one call per cash-down row, and those calls do not
 * compete - each writes its own cell. Stamped individually, the latest-call-wins guard marked every row but the last
 * stale; staleness still works between rounds, where a later edit stamps a higher id.
 */
let gridRoundCounter = 0;
export const nextGridRoundId = (): string => `grid-round-${++gridRoundCounter}`;

export const addPaymentGridMeta = (action: AnyAction, options: AddPaymentGridMetaOptions) => {
    const newAction = {
        ...action,
        meta: {
            ...action.meta,
            paymentGrid: {
                gridAction: true,
                isMainPayment: options.isMainPayment,
                ...(options.mainPaymentActionType && { mainPaymentActionType: options.mainPaymentActionType }),
                ...(options.skipProgramInfoUpdate && { skipProgramInfoUpdate: true }),
                // Omitted unless the caller supplies one, so every existing caller builds an identical action.
                ...(options.gridRoundId && { roundId: options.gridRoundId }),
                ...(options.gridBuildId && { buildId: options.gridBuildId }),
                ...(options.gridRoundTerms?.length && { roundTerms: options.gridRoundTerms }),
                ...(options.gridRoundColumns?.length && { roundColumns: options.gridRoundColumns }),
                ...(typeof options.isUserProgramQuotes === 'boolean' && { isUserProgramQuotes: options.isUserProgramQuotes }),
                ...(options.dealScreenOriginated && { dealScreenOriginated: true })
            }
        }
    };

    if (options.paymentRequestOverride) {
        newAction.meta.paymentRequestOverride = {
            ...newAction.meta?.paymentRequestOverride,
            ...options.paymentRequestOverride
        };
    }

    return newAction;
};

/**
 * A grid-cell payment carries that cell's own term and down payment, so only the main payment of a grid action
 * (and every non-grid payment) describes the deal's own inputs. `isFlexBuyPayment` gates grid-row bookkeeping,
 * not input ownership, so — as with dash-store's `isMainPaymentTrigger` — it is deliberately not tested here.
 */
export const isPrimaryPaymentOption = (triggerAction: AnyAction | undefined): boolean => {
    const { gridAction = false, isMainPayment = false } = triggerAction?.meta?.paymentGrid || {};
    return !gridAction || Boolean(isMainPayment);
};

export const isRegularGridRowAction = (action: AnyAction | undefined): boolean => {
    const paymentGrid = action?.meta?.paymentGrid;
    return paymentGrid?.gridAction === true && !paymentGrid.isFlexBuyPayment;
};

/**
 * Tags a payment-grid action with an explicit target offer type in `meta.middleware.offerType`. The
 * payment-grid middleware reads it (`resolveGridOfferType`) to build the payload for THIS offer type
 * instead of the deal's current one — e.g. driving a LEASE row while the deal is on Finance. The
 * write-side counterpart of `resolveGridOfferType`; for main-grid flows offerType === current offer, so
 * tagging is a no-op there.
 */
export const tagOfferType = (action: AnyAction, offerType: OfferType): AnyAction => ({
    ...action,
    meta: {
        ...action.meta,
        middleware: {
            ...action.meta?.middleware,
            offerType
        }
    }
});

export const addFlexBuyPaymentGridMeta = (action: AnyFSA, options?: AddFlexBuyPaymentGridMetaOptions) => {
    const {
        isMainPayment,
        gridAction = true,
        isFlexBuyPayment = true,
        mainPaymentActionType,
        paymentRequestOverride
    } = options || {};

    const newAction = {
        ...action,
        meta: {
            ...action.meta,
            paymentGrid: {
                gridAction,
                isFlexBuyPayment,
                isMainPayment,
                ...(mainPaymentActionType && { mainPaymentActionType })
            }
        }
    };

    if (paymentRequestOverride) {
        newAction.meta[PAYMENT_REQUEST_OVERRIDE_NODE] = {
            ...newAction.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE],
            ...paymentRequestOverride
        };
    }

    return newAction;
};

export const shouldProcessGridReselectAction = (action: AnyAction) => {
    const isPaymentFailure = action.type === PAYMENT_FAILURE;
    const isReceivedWithError =
        action.type === PAYMENT_RECEIVED &&
        action.meta?.originalAction &&
        action.meta?.offerType &&
        action.payload[action.meta.offerType]?.success !== true;
    const { isMainPayment = false } = action.meta?.originalAction?.meta?.paymentGrid || {};

    return isMainPayment && (isPaymentFailure || isReceivedWithError);
};

export const DOWN_PAYMENT_DIFFERENCE = 2000;

/** Where each deal type's term-id list hangs off a payment response's `terms` node. */
export const RESPONSE_TERM_IDS_KEY: Record<string, string> = {
    [FINANCE]: 'financeTerms',
    [LEASE]: 'leaseTerms',
    [BALLOON]: 'balloonTerms'
};

export type GridRowQuoteOutcome = {
    /** The terms the settling request asked about at this cash-down row. */
    requestedTerms: number[];
    /** Of those, the terms the response actually returned a payment for. */
    pricedTerms: number[];
};

/**
 * Which terms a grid row response came back with a payment for. Read off the RESPONSE, never off merged state:
 * `offer.terms.byId` is merge-only and `preserveOnProgramLoss` keeps the previous record, so a cell asked again and
 * answered with nothing still holds last round's payment there.
 */
export const getPricedTermsFromResponse = (action: AnyAction, offerType: OfferType): number[] => {
    const terms = action?.payload?.terms;
    const byId = terms?.byId;
    if (!byId) return [];

    const offerTypeIds: string[] = terms[RESPONSE_TERM_IDS_KEY[offerType]] ?? [];
    const termIds = offerTypeIds.length ? offerTypeIds : Object.keys(byId);

    return termIds.reduce<number[]>((months, termId) => {
        const term = byId[termId];
        if (typeof term?.months === 'number' && term?.monthlyPayment != null && !months.includes(term.months)) {
            months.push(term.months);
        }
        return months;
    }, []);
};

/**
 * Whether a settled response is an answer about the cells it asked for: a payment that arrived, or a failure
 * that says there is none. An abort was never an answer and a superseded response is discarded, so neither
 * revises what the row holds - see the `PAYMENT_ABORTED` case and `releaseStaleLeaseGridRowLoader`.
 */
const isQuoteBearingResponse = (action: AnyAction): boolean =>
    (action.type === PAYMENT_RECEIVED && !action.meta?.isStalePaymentResponse) || action.type === PAYMENT_FAILURE;

/**
 * The terms one row holds a payment for, carried forward across rounds. `terms` beside it is the CURRENT round's request
 * manifest, replaced on every write - right for scoping a spinner, wrong as a record of what the row holds. A settling
 * request revises only the terms it asked about.
 */
const nextQuotedTerms = (previous: number[] | undefined, outcome?: GridRowQuoteOutcome): number[] | undefined => {
    if (!outcome) return previous;
    const kept = (previous ?? []).filter((term) => !outcome.requestedTerms.includes(term));
    return [...kept, ...outcome.pricedTerms.filter((term) => outcome.requestedTerms.includes(term))];
};

/**
 * spanning both engines is TWO calls at the same key - so a boolean `isLoading` is cleared by whichever half settles
 * first, opening the fallback pass's gate while the other is still out. A count is exact where a term set is not, since
 * two rounds may ask about the same terms. Every grid counts its calls, so a superseded response releases only its own
 * and the row settles with the call that supersedes it. The round and sibling bookkeeping below stays Lease redesign
 * only, detected by `paymentGridData.build`.
 */
const isLeaseRedesignGridData = (paymentGridData: PaymentGridData): boolean => (paymentGridData as any)?.build != null;

const nextPendingCalls = (previous: GenericObject | undefined, isLoading: boolean): number =>
    isLoading ? (previous?.pendingCalls ?? 0) + 1 : Math.max(0, (previous?.pendingCalls ?? 0) - 1);

export const updateLoadingStatus = (
    paymentGridData: PaymentGridData,
    rowKeys: number[],
    terms: number[],
    isLoading: boolean,
    appendTerm = false,
    outcome?: GridRowQuoteOutcome,
    /**
     * The fan-out this request belongs to, so the column view can hold every cell of a round until the whole round has
     * settled. Written on a REQUEST only - the row still belongs to that round while its siblings are out.
     */
    roundId?: string,
    columnIndices?: number[]
) => {
    const isRedesignGrid = isLeaseRedesignGridData(paymentGridData);

    return {
        ...paymentGridData,
        rowsStatuses: rowKeys.reduce((statuses: object, rowIndex: number) => {
            // Cast: `quotedTerms` / `pendingCalls` / `roundId` are not on the published PaymentGridRowStatus type
            // yet, as with `columnSources`. `paymentGridData` is legitimately null/undefined on some callers, and
            // the pre-existing code only dereferenced `rowsStatuses` inside the `appendTerm` branch - so it has to
            // stay optional here too.
            const previous = (paymentGridData?.rowsStatuses as Record<number, GenericObject> | undefined)?.[rowIndex];
            const quotedTerms = nextQuotedTerms(previous?.quotedTerms, outcome);
            const pendingCalls = nextPendingCalls(previous, isLoading);
            const nextRoundId = isRedesignGrid && isLoading && roundId ? roundId : previous?.roundId;

            // A sibling call is already out for this row, so the manifest is the UNION: replacing it dropped the
            // other half's columns out of `requestedTerms`, and their cells stopped spinning mid-flight. A
            // request arriving with nothing pending starts a fresh round and still replaces.
            const isSiblingInFlight = isRedesignGrid && isLoading && (previous?.pendingCalls ?? 0) > 0;
            const nextTerms = appendTerm
                ? [previous?.terms || [], ...terms].flat()
                : isSiblingInFlight
                ? [...new Set([...(previous?.terms ?? []), ...terms])]
                : terms;

            const previousRequestedColumns = previous?.requestedColumns as number[] | undefined;
            const nextRequestedColumns = !columnIndices?.length
                ? previousRequestedColumns
                : isSiblingInFlight || appendTerm
                ? [...new Set([...(previousRequestedColumns ?? []), ...columnIndices])]
                : columnIndices;

            const previousRoundColumns = previous?.roundColumns as number[] | undefined;
            const nextRoundColumns =
                !isRedesignGrid || !columnIndices?.length
                    ? previousRoundColumns
                    : isLoading && !isSiblingInFlight && !appendTerm
                    ? columnIndices
                    : [...new Set([...(previousRoundColumns ?? []), ...columnIndices])];

            const previousLoadingColumns = previous?.loadingColumns as number[] | undefined;
            const loadingColumns = !columnIndices?.length
                ? previousLoadingColumns
                : isLoading
                ? [...new Set([...(previousLoadingColumns ?? []), ...columnIndices])]
                : (previousLoadingColumns ?? []).filter((column) => !columnIndices.includes(column));

            return {
                ...statuses,
                [rowIndex]: {
                    terms: nextTerms,
                    isLoading: pendingCalls > 0 || Boolean(loadingColumns?.length),
                    pendingCalls,
                    ...(quotedTerms && { quotedTerms }),
                    ...(nextRoundId && { roundId: nextRoundId }),
                    ...(loadingColumns?.length ? { loadingColumns } : {}),
                    ...(nextRequestedColumns && { requestedColumns: nextRequestedColumns }),
                    ...(nextRoundColumns && { roundColumns: nextRoundColumns })
                }
            };
        }, paymentGridData?.rowsStatuses || {})
    } as PaymentGridData;
};

export const updatePaymentGridLoadingStatuses = (
    offerType: OfferType,
    paymentGridData: PaymentGridData,
    action: AnyAction,
    isLoading: boolean
) => {
    const cashDown = action.meta?.originalAction?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]?.[offerType]?.downPayment;
    const terms = action.meta?.originalAction?.meta?.[PAYMENT_REQUEST_OVERRIDE_NODE]?.[offerType]?.termList;
    const actionWithMultipleTerms = Array.isArray(terms) && terms.length > 1;
    const shouldAppendTerm = actionWithMultipleTerms && action.meta?.originalAction?.meta?.middleware?.nonSelectedTermHonoringSV;
    const originalAction = action.meta?.originalAction;
    // The fan-out this call belongs to. A discovery carries the build id as its round id, so the whole grid is held.
    const roundId: string | undefined = originalAction?.meta?.paymentGrid?.roundId;

    const allRowKeys = (paymentGridData?.cashDown || []).reduce<number[]>((rows, cash, rowIndex) => {
        if (cash != null) rows.push(rowIndex);
        return rows;
    }, []);

    if (originalAction.type === types.LOAD_PAYMENT_GRID_TERM_LENDER_PROGRAM) {
        return updateLoadingStatus(
            paymentGridData,
            allRowKeys,
            terms,
            isLoading,
            false,
            undefined,
            roundId,
            originalAction?.meta?.gridColumnIndices
        );
    }

    if (originalAction.type === types.LOAD_PAYMENT_GRID_LENDER_PROGRAMS) {
        const termMonthList = [...new Set(paymentGridData?.terms || [])].filter((term) => term !== null) as number[];
        const allColumnIndices = (paymentGridData?.terms || []).reduce<number[]>((covered, term, columnIndex) => {
            if (term != null) covered.push(columnIndex);
            return covered;
        }, []);
        return updateLoadingStatus(
            paymentGridData,
            allRowKeys,
            termMonthList,
            isLoading,
            false,
            undefined,
            roundId,
            allColumnIndices
        );
    }

    /**
     * Only a settling ROW payment revises the quote ledger, and only for the terms it asked about: the cells go on showing
     * what they showed until an answer arrives.
     */
    const outcome: GridRowQuoteOutcome | undefined =
        !isLoading && Array.isArray(terms) && isQuoteBearingResponse(action)
            ? { requestedTerms: terms, pricedTerms: getPricedTermsFromResponse(action, offerType) }
            : undefined;

    const requestedRowKeys: number[] = originalAction?.meta?.gridRowIndices?.length
        ? originalAction.meta.gridRowIndices
        : (paymentGridData?.cashDown || []).reduce<number[]>((rows, cash, rowIndex) => {
              if (cash === cashDown) rows.push(rowIndex);
              return rows;
          }, []);

    return updateLoadingStatus(
        paymentGridData,
        requestedRowKeys,
        terms,
        isLoading,
        shouldAppendTerm,
        outcome,
        roundId,
        originalAction?.meta?.gridColumnIndices
    );
};

export const generateFlexBuyPaymentGridData = (
    flexBuyDownPayments: number[],
    flexBuyTerms: number[]
): UpdateFlexBuyPaymentGridDataPayload => {
    return flexBuyDownPayments.map((downPayment, index) => {
        return {
            key: index,
            value: downPayment,
            items: flexBuyTerms.map(() => {
                return {
                    id: `${index}`,
                    isLoading: true
                } as FlexBuyPaymentGridDataItems[number];
            })
        };
    });
};

// Typescript types added as a part of dr-offer-redux refactoring by Team Falcon
export const updateFlexBuyPaymentGridDataByColumn = (
    flexBuyPaymentGridData: UpdateFlexBuyPaymentGridDataPayload,
    columnInd: number
) => {
    return flexBuyPaymentGridData.map((item) => {
        return {
            ...item,
            items: item.items.map((subItem, index) => {
                if (index !== columnInd) {
                    return subItem;
                }
                return {
                    ...subItem,
                    isLoading: true
                };
            })
        };
    });
};

export const updateFlexBuyPaymentGridDataByRow = (flexBuyPaymentGridData: UpdateFlexBuyPaymentGridDataPayload, rowInd: number) => {
    return flexBuyPaymentGridData.map((item, index) => {
        return {
            ...item,
            items: item.items.map((subItem) => {
                if (index !== rowInd) {
                    return subItem;
                }
                return {
                    ...subItem,
                    isLoading: true
                };
            })
        };
    });
};
